// store/admin/college-management/store/_saga.js

import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actions from './_actions';
import * as types from './_actionTypes';
import { CollegeHelper } from '../../../../utils/API/BackendHelper/admin';
import CollegeDegreeHelper  from '../../../../utils/API/BackendHelper/college/degrees';
import API_ENDPOINTS from '../../../../utils/API/endpoints';
import ApiHelper from '../../../../utils/API/ApiHelper';


function* createCollegeSaga(action) {
    try {
        const formData = action.payload;

        // Pass FormData directly to API (supports both file uploads and regular data)
        const response = yield call(CollegeHelper.createCollege, formData);

        if (response.success) {
            const collegeData = response.data.college || response.data;
            const principalData = response.data.principal;

            const newCollege = {
                id: collegeData.id,
                name: collegeData.name || collegeData.collegeName,
                code: collegeData.code,
                address: collegeData.address,
                contact_email: collegeData.contact_email || collegeData.email,
                contact_phone: collegeData.contact_phone || collegeData.phone,
                website: collegeData.website,
                affiliated_university: collegeData.affiliated_university || collegeData.affiliatedUniversity,
                accreditation: collegeData.accreditation,
                type: collegeData.type || collegeData.collegeType,
                description: collegeData.description,
                established_year: collegeData.established_year || collegeData.establishedYear,
                subscription_active: collegeData.subscription_active,
                subscription_expires_at: collegeData.subscription_expires_at,
                created_at: collegeData.created_at,
                updated_at: collegeData.updated_at,
                studentsCount: collegeData.studentsCount || 0,
                certificatesIssued: collegeData.certificatesIssued || 0,
                email: collegeData.contact_email || collegeData.email,
                phone: collegeData.contact_phone || collegeData.phone,
                status: collegeData.subscription_active ? 'approved' : 'pending',
                principal: principalData,
                principal_name: principalData?.principal_name,
                signature_url: principalData?.signature_url
            };

            yield put(actions.createCollegeSuccess(newCollege));

            // Trigger navigation to college management page
            yield put({ type: 'NAVIGATE_TO_COLLEGE_MANAGEMENT' });

        } else {
            yield put(actions.createCollegeFailure(response.message || 'Failed to create college'));
        }
    } catch (error) {
        yield put(actions.createCollegeFailure('An unexpected error occurred'));
    }
}

function* updateCollegeSaga(action) {
    try {
        const { collegeId, updateData } = action.payload;

        // Check if updateData is FormData (for file uploads)
        const isFormData = updateData instanceof FormData;

        let backendData;

        if (isFormData) {
            // FormData is passed directly - backend will handle multipart/form-data
            backendData = updateData;
            console.log('Updating college with FormData (includes file upload)');
        } else {
            // Transform to backend format (matching backend validation exactly)
            backendData = {};

            // Map frontend fields to backend validation format
            if (updateData.collegeName !== undefined && updateData.collegeName.trim()) {
                backendData.name = updateData.collegeName.trim();
            }
            if (updateData.email !== undefined && updateData.email.trim()) {
                backendData.contactEmail = updateData.email.trim();
            }
            if (updateData.phone !== undefined && updateData.phone.trim()) {
                backendData.contactPhone = updateData.phone.trim();
            }
            if (updateData.address !== undefined && updateData.address.trim()) {
                backendData.address = updateData.address.trim();
            }
            if (updateData.collegeType !== undefined) {
                backendData.type = updateData.collegeType;
            }
            if (updateData.website !== undefined && updateData.website.trim()) {
                backendData.website = updateData.website.trim();
            }
            if (updateData.affiliated_university !== undefined && updateData.affiliated_university.trim()) {
                backendData.affiliatedUniversity = updateData.affiliated_university.trim();
            }
            if (updateData.accreditation !== undefined && updateData.accreditation.trim()) {
                backendData.accreditation = updateData.accreditation.trim();
            }
            if (updateData.description !== undefined && updateData.description.trim()) {
                backendData.description = updateData.description.trim();
            }
            if (updateData.established_year !== undefined) {
                const year = parseInt(updateData.established_year);
                if (!isNaN(year) && year >= 1800 && year <= new Date().getFullYear()) {
                    backendData.establishedYear = year;
                }
            }
            if (updateData.code !== undefined && updateData.code.trim()) {
                backendData.code = updateData.code.trim().toUpperCase();
            }
            if (updateData.logoUrl !== undefined && updateData.logoUrl.trim()) {
                backendData.logoUrl = updateData.logoUrl.trim();
            }
            if (updateData.subscriptionActive !== undefined) {
                backendData.subscriptionActive = Boolean(updateData.subscriptionActive);
            }
            if (updateData.subscriptionExpiresAt !== undefined && updateData.subscriptionExpiresAt) {
                backendData.subscriptionExpiresAt = updateData.subscriptionExpiresAt;
            }
            if (updateData.principalName !== undefined && updateData.principalName.trim()) {
                backendData.principalName = updateData.principalName.trim();
            }
            if (updateData.signatureUrl !== undefined && updateData.signatureUrl.trim()) {
                backendData.signatureUrl = updateData.signatureUrl.trim();
            }

            console.log('Updating college:', collegeId, 'with data:', backendData);
        }

        const response = yield call(CollegeHelper.updateCollege, collegeId, backendData);

        console.log('Update college response:', response);

        if (response.success) {
            const collegeData = response.data.college || response.data;
            const principalData = response.data.principal || response.data.newPrincipal;

            const updatedCollege = {
                id: collegeData.id,
                name: collegeData.name || collegeData.collegeName,
                code: collegeData.code,
                address: collegeData.address,
                contact_email: collegeData.contact_email || collegeData.email,
                contact_phone: collegeData.contact_phone || collegeData.phone,
                website: collegeData.website,
                affiliated_university: collegeData.affiliated_university || collegeData.affiliatedUniversity,
                accreditation: collegeData.accreditation,
                type: collegeData.type || collegeData.collegeType,
                description: collegeData.description,
                established_year: collegeData.established_year || collegeData.establishedYear,
                subscription_active: collegeData.subscription_active,
                subscription_expires_at: collegeData.subscription_expires_at,
                created_at: collegeData.created_at,
                updated_at: collegeData.updated_at,
                studentsCount: collegeData.studentsCount || 0,
                certificatesIssued: collegeData.certificatesIssued || 0,
                email: collegeData.contact_email || collegeData.email,
                phone: collegeData.contact_phone || collegeData.phone,
                status: collegeData.subscription_active ? 'approved' : 'pending',
                principal: principalData,
                principal_name: principalData?.principal_name,
                signature_url: principalData?.signature_url
            };

            yield put(actions.updateCollegeSuccess(updatedCollege));
        } else {
            yield put(actions.updateCollegeFailure(response.message || 'Failed to update college'));
        }
    } catch (error) {
        console.error('Unexpected error in updateCollegeSaga:', error);
        yield put(actions.updateCollegeFailure(error.message || 'An unexpected error occurred'));
    }
}



function* fetchCollegesSaga(action) {
    try {
        // Use CollegeHelper for consistency with create operation
        const response = yield call(CollegeHelper.getAllColleges);

        console.log('Fetch colleges response:', response);

        if (response.success) {
            // Handle the response based on your API structure
            const collegesData = response.data || [];

            const colleges = collegesData.map(college => ({
                id: college.id,
                name: college.name || college.collegeName,
                code: college.code || '',
                address: college.address || '',
                contact_email: college.contact_email || college.email,
                contact_phone: college.contact_phone || college.phone,
                website: college.website || '',
                affiliated_university: college.affiliated_university || college.affiliatedUniversity || '',
                accreditation: college.accreditation || '',
                type: college.type || college.collegeType || 'private',
                description: college.description || '',
                established_year: college.established_year || college.establishedYear,
                subscription_active: college.subscription_active || false,
                subscription_expires_at: college.subscription_expires_at,
                created_at: college.created_at,
                updated_at: college.updated_at,
                studentsCount: college.studentsCount || 0,
                certificatesIssued: college.certificatesIssued || 0,
                email: college.contact_email || college.email,
                phone: college.contact_phone || college.phone,
                status: college.subscription_active ? 'approved' : 'pending',
                principal: college.principal || null,  // Include principal data
                principal_name: college.principal?.principal_name,
                signature_url: college.principal?.signature_url
            }));

            yield put(actions.fetchCollegesSuccess(colleges));
        } else {
            yield put(actions.fetchCollegesFailure(response.message || 'Failed to fetch colleges'));
        }
    } catch (error) {
        console.error('Unexpected error in fetchCollegesSaga:', error);
        yield put(actions.fetchCollegesFailure(error.message || 'An unexpected error occurred'));
    }
}

// function* approveCollegeSaga(action) {
//     try {
//         const collegeId = action.payload;
//         const response = yield call(ApiHelper.patch, API_ENDPOINTS.ADMIN.COLLEGE_APPROVE(collegeId));

//         if (response.success) {
//             yield put(actions.approveCollegeSuccess(collegeId));
//         } else {
//             yield put(actions.approveCollegeFailure(response.message || 'Failed to approve college', collegeId));
//         }
//     } catch (error) {
//         console.error('Unexpected error in approveCollegeSaga:', error);
//         yield put(actions.approveCollegeFailure('An unexpected error occurred', action.payload));
//     }
// }

function* fetchDegreesForCollegeSaga(action) {
    try {
        const collegeId = action.payload;
        // Replace with your actual API call
        const response = yield call(CollegeDegreeHelper.getDegreesOffered, collegeId); // `api.fetchDegreesForCollege` should hit your backend
        yield put(actions.fetchDegreesForCollegeSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchDegreesForCollegeFailure(error.message, action.payload));
    }
}


function* deleteCollegeSaga(action) {
    try {
        const collegeId = action.payload;
        const response = yield call(ApiHelper.delete, API_ENDPOINTS.ADMIN.COLLEGE_BY_ID(collegeId));

        if (response.success) {
            yield put(actions.deleteCollegeSuccess(collegeId));
        } else {
            yield put(actions.deleteCollegeFailure(response.message || 'Failed to delete college', collegeId));
        }
    } catch (error) {
        console.error('Unexpected error in deleteCollegeSaga:', error);
        yield put(actions.deleteCollegeFailure('An unexpected error occurred', action.payload));
    }
}

function* toggleCollegeSubscriptionSaga(action) {
    try {
        const collegeId = action.payload;
        const response = yield call(CollegeHelper.toggleCollegeSubscription, collegeId);

        if (response.success) {
            const subscriptionActive = response.data.subscription_active;
            yield put(actions.toggleCollegeSubscriptionSuccess(collegeId, subscriptionActive));
        } else {
            yield put(actions.toggleCollegeSubscriptionFailure(
                response.message || 'Failed to toggle subscription',
                collegeId
            ));
        }
    } catch (error) {
        console.error('Unexpected error in toggleCollegeSubscriptionSaga:', error);
        yield put(actions.toggleCollegeSubscriptionFailure(
            'An unexpected error occurred',
            action.payload
        ));
    }
}

function* adminCollegeSaga() {
    yield takeEvery(types.FETCH_COLLEGES_REQUEST, fetchCollegesSaga);
    yield takeEvery(types.CREATE_COLLEGE_REQUEST, createCollegeSaga);
    yield takeEvery(types.UPDATE_COLLEGE_REQUEST, updateCollegeSaga);
    // yield takeEvery(types.APPROVE_COLLEGE_REQUEST, approveCollegeSaga);
    yield takeLatest(types.FETCH_DEGREES_FOR_COLLEGE_REQUEST, fetchDegreesForCollegeSaga);
    yield takeEvery(types.DELETE_COLLEGE_REQUEST, deleteCollegeSaga);
    yield takeEvery(types.TOGGLE_COLLEGE_SUBSCRIPTION_REQUEST, toggleCollegeSubscriptionSaga );
}

export default adminCollegeSaga;
